import { SelectionModel } from '@angular/cdk/collections';
import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, TemplateRef } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { Debounce } from '@core';
import { type DeviceData, DeviceTableCols, type NormalizedDeviceData } from '@devices/models';
import { DeviceServiceV2 } from '@devices/services';
import { type IPageInfo, VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { TableSearchInputComponent } from '@ui';

import { FiltersBarComponent } from '../../filters';

export interface DevicePickerItemContext {
  $implicit: NormalizedDeviceData;
}

const SCROLL_THRESHOLD = 5;

@Component({
  imports: [TableSearchInputComponent, FiltersBarComponent, VirtualScrollerModule, MatCheckbox, NgTemplateOutlet],
  selector: 'avs-device-picker',
  styleUrl: './device-picker.component.scss',
  templateUrl: './device-picker.component.html',
})
export class DevicePickerComponent {
  masterDevice = input<DeviceData>();
  assignedDevices = input<NormalizedDeviceData[]>([]);
  itemTemplate = input<TemplateRef<DevicePickerItemContext>>();
  isMultiSelect = input(true);
  initialFilters = input<Record<string, string | string[] | boolean>>();
  apiFilters = input<Record<string, string | string[] | boolean>>();
  showFiltersBar = input(true);
  sessionFiltersId = input.required<string>();
  scrollBlockHeight = input('404px');

  filtersBarValue = computed(() => {
    return {
      building: this.masterDevice()?.locationId,
      ...this.initialFilters(),
    };
  });
  selectedDevicesChange = output<NormalizedDeviceData[]>();
  protected devices = signal<NormalizedDeviceData[]>([]);
  protected filters = signal<object>({});
  protected shadowShown = signal(false);
  protected selectedDevices = new Map<string, NormalizedDeviceData>();
  protected selection = new SelectionModel<string>(true, []);

  private count = 0;
  private loading = false;
  private offset = 0;
  private query = '';

  private deviceServiceV2 = inject(DeviceServiceV2);

  constructor() {
    effect(() => {
      const assignedDevices = this.assignedDevices();

      this.selection.clear();
      this.selection.select(...assignedDevices.map((device) => device.id));
      this.selectedDevices = new Map(assignedDevices.map((device) =>
        [device.id, device],
      ));
    });
  }

  @Debounce()
  protected fetchMore(event: IPageInfo) {
    if (event.endIndex > this.devices().length - SCROLL_THRESHOLD && this.count > this.devices().length && !this.loading) {
      this.load();
    }
  }

  protected filtersChanged(filters: object) {
    this.filters.set(filters);
    this.count = 0;
    this.offset = 0;
    this.devices.set([]);
    this.load();
  }

  @Debounce()
  protected queryChanged(query: string) {
    this.query = query;
    this.count = 0;
    this.offset = 0;
    this.devices.set([]);
    this.load();
  }

  protected toggleDevice(device: NormalizedDeviceData) {
    if (this.selection.isSelected(device.id)) {
      this.selectedDevices.delete(device.id);
    } else {
      this.selectedDevices.set(device.id, device);
    }
    this.selection.toggle(device.id);
    this.selectedDevicesChange.emit([...this.selectedDevices.values()]);
  }

  protected selectDevice(device: NormalizedDeviceData) {
    this.selection.clear();
    this.selectedDevices.clear();

    this.selection.select(device.id);
    this.selectedDevices.set(device.id, device);

    this.selectedDevicesChange.emit([...this.selectedDevices.values()]);
  }

  private load() {
    this.loading = true;
    this.deviceServiceV2.list({
      filters: { ...this.filters(), ...this.apiFilters() },
      limit: 50,
      offset: this.offset,
      query: this.query,
      sort: {
        active: DeviceTableCols.DEVICE_NAME,
        direction: 'asc',
      },
    }).subscribe(({ count, data }) => {
      this.loading = false;
      this.count = count;
      this.offset += data.length;
      this.devices.set([
        ...this.devices(),
        ...data,
      ]);
    });
  }
}
